import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Package, Factory, Printer, Edit2, CalendarDays, Trash2, FileDown, History, CheckCircle, XCircle, Mail, Send, Eye, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { orderService, orderLineService, productService, productRevisionService, productionLotService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PrintableOrder from '../components/PrintableOrder';
import { useReactToPrint } from 'react-to-print';
import RawAvailabilityPanel from '../components/raw/RawAvailabilityPanel';

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [expandedRawLine, setExpandedRawLine] = useState(null);
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lineDialogOpen, setLineDialogOpen] = useState(false);
  const [editLineDialogOpen, setEditLineDialogOpen] = useState(false);
  const [lotDialogOpen, setLotDialogOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [triggerPrint, setTriggerPrint] = useState(false);
  const { hasRole } = useAuth();
  const printRef = useRef(null);
  const [exportHistory, setExportHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewType, setEmailPreviewType] = useState('confirmation');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [lineForm, setLineForm] = useState({
    product_id: '',
    product_revision_id: '',
    quantity: '',
    unit_price: '',
    delivery_date_required: '',
    customer_lot: '',
    notes: ''
  });

  const [editLineForm, setEditLineForm] = useState({
    delivery_date_required: '',
    quantity: '',
    unit_price: '',
    customer_lot: '',
    notes: ''
  });

  const [lotForm, setLotForm] = useState({
    quantity: '',
    opening_date: new Date().toISOString().split('T')[0],
    responsible_operator: '',
    notes: ''
  });

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: `Ordine_${order?.order_number || ''}` });

  useEffect(() => {
    if (triggerPrint && printData) {
      const timer = setTimeout(() => {
        handlePrint();
        setTriggerPrint(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [triggerPrint, printData]);

  useEffect(() => {
    loadOrder();
    loadProducts();
    loadExportHistory();
    loadEmailLog();
  }, [id]);

  const loadOrder = async () => {
    try {
      const [orderRes, linesRes] = await Promise.all([
        orderService.getById(id),
        orderLineService.getAll({ order_id: id })
      ]);
      setOrder(orderRes.data);
      setOrderLines(linesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento ordine');
    } finally {
      setLoading(false);
    }
  };

  const loadExportHistory = async () => {
    try {
      const res = await orderService.getExportHistory(id);
      setExportHistory(res.data);
    } catch { /* ignore */ }
  };

  const loadEmailLog = async () => {
    try {
      const res = await orderService.getEmailLog(id);
      setEmailLog(res.data);
    } catch { /* ignore */ }
  };

  const handleSendConfirmation = async () => {
    if (!window.confirm('Inviare l\'email di conferma ordine al cliente?')) return;
    setSendingEmail(true);
    try {
      const res = await orderService.sendConfirmation(id, emailPreviewType);
      toast.success(res.data.message || 'Email inviata con successo');
      setEmailPreviewOpen(false);
      loadEmailLog();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore invio email';
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailPreview = async (type = 'confirmation') => {
    setEmailPreviewType(type);
    setLoadingPreview(true);
    setEmailPreviewOpen(true);
    try {
      const res = await orderService.getEmailPreview(id, type);
      setEmailPreview(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore caricamento anteprima';
      toast.error(msg);
      setEmailPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getAll({ status: 'active' });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  // Filter products by the order's client
  const clientProducts = order?.client_id
    ? products.filter(p => p.client_id === order.client_id)
    : products;

  const loadRevisions = async (productId) => {
    try {
      const response = await productRevisionService.getAll({ product_id: productId });
      setRevisions(response.data);
    } catch (error) {
      console.error('Failed to load revisions');
    }
  };

  const handleProductChange = (productId) => {
    const selectedProduct = clientProducts.find(p => p.id === productId);
    setLineForm({
      ...lineForm,
      product_id: productId,
      product_revision_id: '',
      unit_price: selectedProduct?.unit_price != null ? selectedProduct.unit_price : lineForm.unit_price,
    });
    loadRevisions(productId);
  };

  const handleCreateLine = async (e) => {
    e.preventDefault();
    try {
      const data = {
        order_id: id,
        product_id: lineForm.product_id,
        product_revision_id: lineForm.product_revision_id || null,
        quantity: parseInt(lineForm.quantity),
        unit_price: lineForm.unit_price ? parseFloat(lineForm.unit_price) : null,
        delivery_date_required: lineForm.delivery_date_required || null,
        customer_lot: lineForm.customer_lot?.trim() || null,
        notes: lineForm.notes || null
      };
      await orderLineService.create(data);
      toast.success('Riga ordine aggiunta');
      setLineDialogOpen(false);
      setLineForm({ product_id: '', product_revision_id: '', quantity: '', unit_price: '', delivery_date_required: '', customer_lot: '', notes: '' });
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta riga');
    }
  };

  const handleUpdateLine = async (e) => {
    e.preventDefault();
    if (!selectedLine) return;
    try {
      const data = {};
      if (editLineForm.delivery_date_required) data.delivery_date_required = editLineForm.delivery_date_required;
      if (editLineForm.quantity) data.quantity = parseInt(editLineForm.quantity);
      if (editLineForm.unit_price) data.unit_price = parseFloat(editLineForm.unit_price);
      if (editLineForm.notes !== undefined) data.notes = editLineForm.notes || null;
      data.customer_lot = editLineForm.customer_lot?.trim() || '';
      await orderLineService.update(selectedLine.id, data);
      toast.success('Riga ordine aggiornata');
      setEditLineDialogOpen(false);
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento riga');
    }
  };

  const handleCreateLot = async (e) => {
    e.preventDefault();
    try {
      const data = {
        order_line_id: selectedLine.id,
        product_id: selectedLine.product_id,
        product_revision_id: selectedLine.product_revision_id,
        quantity: parseInt(lotForm.quantity),
        opening_date: lotForm.opening_date,
        responsible_operator: lotForm.responsible_operator || null,
        notes: lotForm.notes || null
      };
      await productionLotService.create(data);
      toast.success('Lotto di produzione creato');
      setLotDialogOpen(false);
      setLotForm({ quantity: '', opening_date: new Date().toISOString().split('T')[0], responsible_operator: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione lotto');
    }
  };


  const handleDeleteLine = async (line) => {
    if (!window.confirm(`Eliminare la riga ${line.line_number} (${line.product_code || ''})?`)) return;
    try {
      await orderLineService.delete(line.id);
      toast.success('Riga eliminata');
      loadOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const openLotDialog = (line) => {
    setSelectedLine(line);
    setLotForm({ ...lotForm, quantity: line.quantity.toString() });
    setLotDialogOpen(true);
  };

  const openEditLineDialog = (line) => {
    setSelectedLine(line);
    setEditLineForm({
      delivery_date_required: line.delivery_date_required || '',
      quantity: line.quantity?.toString() || '',
      unit_price: line.unit_price?.toString() || '',
      customer_lot: line.customer_lot || '',
      notes: line.notes || ''
    });
    setEditLineDialogOpen(true);
  };

  const handlePrintOrder = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('evotech_token');
      const res = await fetch(`${API_URL}/api/orders/${id}/print-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Errore');
      const data = await res.json();
      setPrintData(data);
      setTriggerPrint(true);
    } catch (error) {
      toast.error('Errore nel caricamento dati di stampa');
    }
  };

  const handleExportEasyfatt = async () => {
    setExporting(true);
    try {
      // Validate first
      const valRes = await orderService.validateExport(id);
      if (!valRes.data.valid) {
        toast.error(valRes.data.message || 'Ordine non valido per esportazione');
        return;
      }
      // Export
      const res = await orderService.exportEasyfatt(id);
      const blob = new Blob([res.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ordine_${order?.order_number?.replace(/\//g, '-') || 'N'}_Easyfatt.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Esportazione Easyfatt completata');
      loadExportHistory();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore esportazione';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      open: 'bg-sky-500 text-white',
      in_production: 'bg-amber-500 text-white',
      partial: 'bg-violet-500 text-white',
      closed: 'bg-slate-400 text-white',
      pending: 'bg-slate-200 text-slate-700',
    };
    return map[status] || map.pending;
  };

  const statusLabel = (status) => {
    const map = { open: 'Aperto', in_production: 'In Produzione', partial: 'Parziale', closed: 'Chiuso', pending: 'In Attesa' };
    return map[status] || status;
  };

  const priorityLabel = (p) => {
    const map = { normal: 'Normale', high: 'Alta', urgent: 'Urgente' };
    return map[p] || p;
  };

  const getPriorityBadge = (priority) => {
    const map = {
      normal: 'bg-slate-200 text-slate-700',
      high: 'bg-orange-500 text-white',
      urgent: 'bg-red-600 text-white',
    };
    return map[priority] || map.normal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Ordine non trovato</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Hidden print area */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {printData && <PrintableOrder data={printData} />}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/orders">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-mono">{order.order_number}</h1>
              <Badge className={getStatusBadge(order.status)}>
                {statusLabel(order.status)}
              </Badge>
              <Badge className={getPriorityBadge(order.priority)}>
                {priorityLabel(order.priority)}
              </Badge>
            </div>
            <p className="text-slate-500 mt-1">{order.client_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openEmailPreview('confirmation')} variant="outline" className="h-11 gap-2" disabled={order.status === 'cancelled'} data-testid="send-confirmation-btn">
            <Mail className="w-4 h-4" />
            Invia Conferma
          </Button>
          <Button onClick={handleExportEasyfatt} variant="outline" className="h-11 gap-2" disabled={exporting || order.status === 'cancelled'} data-testid="export-easyfatt-btn">
            <FileDown className="w-4 h-4" />
            {exporting ? 'Esportazione...' : 'Esporta per Easyfatt'}
          </Button>
          <Button onClick={handlePrintOrder} variant="outline" className="h-11 gap-2" data-testid="print-order-btn">
            <Printer className="w-4 h-4" />
            Stampa Ordine
          </Button>
        </div>
      </div>

      {/* Order Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dettagli Ordine</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Riferimento Cliente</p>
              <p className="font-medium">{order.customer_order_ref || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Data Ordine</p>
              <p>{order.order_date}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Consegna Richiesta</p>
              <p>{order.required_delivery_date || '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Note</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">{order.notes || 'Nessuna nota'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Order Lines */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Righe Ordine</CardTitle>
          {hasRole('admin', 'quality', 'production') && (
            <Button size="sm" onClick={() => setLineDialogOpen(true)} data-testid="add-line-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Riga
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {orderLines.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna riga ordine</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Prodotto</th>
                    <th>Revisione</th>
                    <th>Lotto Cliente</th>
                    <th>Quantita</th>
                    <th>Consegnata</th>
                    <th>Residuo</th>
                    <th>Evasione</th>
                    <th>Data Consegna</th>
                    <th>Stato</th>
                    <th>Commessa</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderLines.map((line) => {
                    const isLate = line.delivery_date_required
                      && new Date(line.delivery_date_required) < new Date()
                      && !['completed', 'closed', 'delivered'].includes(line.status);
                    const lotsDone = line.production_lots?.every(l => l.status === 'closed' || l.status === 'completed');
                    const showLate = isLate && !lotsDone;
                    return (
                    <React.Fragment key={line.id}>
                    <tr data-testid={`order-line-${line.id}`} className={showLate ? 'bg-red-50' : ''}>
                      <td className="font-mono">{line.line_number}</td>
                      <td>
                        <div>
                          <p className="font-mono text-sm font-medium">{line.product_code}</p>
                          <p className="text-xs text-slate-500">{line.product_name}</p>
                        </div>
                      </td>
                      <td className="font-mono text-sm">{line.revision_code || '-'}</td>
                      <td data-testid={`customer-lot-${line.id}`}>
                        {line.customer_lot ? (
                          <span className="font-mono text-xs font-semibold bg-sky-50 text-sky-900 border border-sky-200 rounded px-1.5 py-0.5">
                            {line.customer_lot}
                          </span>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                      <td className="font-medium">{line.quantity}</td>
                      <td className="text-center">
                        <span className={`font-bold ${line.delivered_quantity > 0 ? (line.overdelivery_quantity > 0 ? 'text-amber-700' : 'text-emerald-700') : 'text-slate-400'}`} data-testid={`delivered-${line.id}`}>
                          {line.delivered_quantity || 0}
                        </span>
                        {line.overdelivery_quantity > 0 && (
                          <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-mono" data-testid={`overdelivery-badge-${line.id}`}>
                            Sovraconsegna +{line.overdelivery_quantity}
                          </Badge>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={`font-medium ${line.remaining_quantity > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                          {line.remaining_quantity ?? line.quantity}
                        </span>
                      </td>
                      <td>
                        <Badge className={
                          line.fulfillment_status === 'evaso_sovraconsegna' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                          line.fulfillment_status === 'evaso' ? 'bg-emerald-100 text-emerald-800' :
                          line.fulfillment_status === 'parzialmente_evaso' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-100 text-slate-600'
                        } data-testid={`fulfillment-${line.id}`}>
                          {line.fulfillment_status === 'evaso_sovraconsegna' ? 'Evaso con sovraconsegna' :
                           line.fulfillment_status === 'evaso' ? 'Evaso' :
                           line.fulfillment_status === 'parzialmente_evaso' ? 'Parzialmente evaso' : 'Da evadere'}
                        </Badge>
                      </td>
                      <td>
                        {line.delivery_date_required ? (
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center gap-1 text-sm ${showLate ? 'text-red-600 font-semibold' : ''}`}>
                              <CalendarDays className={`w-3.5 h-3.5 ${showLate ? 'text-red-500' : 'text-slate-400'}`} />
                              {line.delivery_date_required}
                            </span>
                            {showLate && (
                              <Badge className="bg-red-600 text-white text-[10px] w-fit" data-testid={`late-badge-${line.id}`}>In Ritardo</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        <Badge className={getStatusBadge(line.status)}>
                          {statusLabel(line.status)}
                        </Badge>
                      </td>
                      <td>
                        {line.production_lots && line.production_lots.length > 0 ? (
                          <div className="space-y-1">
                            {line.production_lots.map((lot) => (
                              <a key={lot.id} href={`/production-lots/${lot.id}`} className="flex items-center gap-2 group hover:bg-slate-50 rounded px-1 py-0.5" data-testid={`lot-link-${lot.id}`}>
                                <span className="font-mono text-xs font-medium text-blue-600 group-hover:underline">{lot.lot_code}</span>
                                <Badge className={
                                  lot.status === 'closed' || lot.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  lot.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                  lot.status === 'suspended' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }>
                                  {lot.status === 'open' ? 'Aperto' :
                                   lot.status === 'in_progress' ? 'In Corso' :
                                   lot.status === 'closed' ? 'Chiuso' :
                                   lot.status === 'completed' ? 'Completato' :
                                   lot.status === 'suspended' ? 'Sospeso' : lot.status}
                                </Badge>
                                <span className="text-xs text-slate-500">{lot.good_quantity || 0}/{lot.quantity}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Nessuna</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedRawLine(expandedRawLine === line.id ? null : line.id)}
                            data-testid={`toggle-raw-${line.id}`}
                            title="Disponibilità grezzo"
                          >
                            {expandedRawLine === line.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </Button>
                          {hasRole('admin', 'quality', 'production') && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditLineDialog(line)}
                                data-testid={`edit-line-${line.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openLotDialog(line)}
                                data-testid={`create-lot-${line.id}`}
                              >
                                <Factory className="w-4 h-4 mr-1" />
                                Crea Lotto
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteLine(line)}
                                data-testid={`delete-line-${line.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRawLine === line.id && (
                      <tr data-testid={`raw-row-${line.id}`}>
                        <td colSpan={12} className="bg-slate-50 p-3">
                          <RawAvailabilityPanel
                            productId={line.product_id}
                            orderLineId={line.id}
                            requiredQty={line.remaining_quantity ?? line.quantity}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export History */}
      {exportHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" />
              Storico Esportazioni Easyfatt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data Esportazione</th>
                    <th>Esportato da</th>
                    <th>Riferimento Ordine</th>
                    <th>Righe</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {exportHistory.map(h => (
                    <tr key={h.id} data-testid={`export-history-${h.id}`}>
                      <td className="text-sm">{new Date(h.export_date).toLocaleString('it-IT')}</td>
                      <td className="text-sm">{h.exported_by_name || '-'}</td>
                      <td className="font-mono text-sm">{h.order_number}</td>
                      <td className="text-sm">{h.lines_count}</td>
                      <td>
                        <Badge className="bg-emerald-100 text-emerald-800 gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {h.status === 'completato' ? 'Completato' : h.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Log */}
      {emailLog.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Storico Email Inviate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data Invio</th>
                    <th>Tipo</th>
                    <th>Destinatario</th>
                    <th>Oggetto</th>
                    <th>Inviata da</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLog.map(e => (
                    <tr key={e.id} data-testid={`email-log-${e.id}`}>
                      <td className="text-sm">{new Date(e.sent_at).toLocaleString('it-IT')}</td>
                      <td className="text-sm">
                        <Badge className={e.email_type === 'delivery_change' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>
                          {e.email_type === 'delivery_change' ? 'Modifica Consegna' : e.email_type === 'test' ? 'Test' : 'Conferma Ordine'}
                        </Badge>
                      </td>
                      <td className="text-sm">{e.recipient}</td>
                      <td className="text-sm">{e.subject}</td>
                      <td className="text-sm">{e.sent_by}</td>
                      <td>
                        {e.status === 'sent' ? (
                          <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle className="w-3 h-3" />Inviata</Badge>
                        ) : e.status === 'failed' ? (
                          <span className="flex items-center gap-1 text-xs text-red-600" title={e.error_message || ''}>
                            <AlertCircle className="w-3 h-3" />Errore
                          </span>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">{e.status || 'N/A'}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Preview Modal */}
      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Anteprima Email
            </DialogTitle>
          </DialogHeader>
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : emailPreview ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-400 text-xs uppercase tracking-wide block">Destinatario</span>
                  <span className="font-medium">{emailPreview.recipient || 'Nessun indirizzo email'}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs uppercase tracking-wide block">Oggetto</span>
                  <span className="font-medium">{emailPreview.subject}</span>
                </div>
              </div>
              {!emailPreview.recipient && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700" data-testid="no-email-warning">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Il cliente non ha un indirizzo email registrato. Aggiornare l&apos;anagrafica.
                </div>
              )}
              <div className="flex-1 overflow-auto border border-slate-200 rounded-lg" data-testid="email-preview-body">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailPreview.html || '') }} className="p-4" />
              </div>
            </div>
          ) : null}
          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setEmailPreviewOpen(false)}>Chiudi</Button>
            <Button onClick={handleSendConfirmation} disabled={sendingEmail || !emailPreview?.recipient} data-testid="confirm-send-email-btn">
              <Send className="w-4 h-4 mr-2" />
              {sendingEmail ? 'Invio in corso...' : 'Invia Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Line Dialog */}
      <Dialog open={lineDialogOpen} onOpenChange={setLineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Riga Ordine</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLine}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Prodotto *</Label>
                <Select value={lineForm.product_id} onValueChange={handleProductChange} required>
                  <SelectTrigger data-testid="line-product-select">
                    <SelectValue placeholder="Seleziona prodotto" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.code} - {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {revisions.length > 0 && (
                <div className="space-y-2">
                  <Label>Revisione</Label>
                  <Select
                    value={lineForm.product_revision_id}
                    onValueChange={(value) => setLineForm({ ...lineForm, product_revision_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona revisione" />
                    </SelectTrigger>
                    <SelectContent>
                      {revisions.map((rev) => (
                        <SelectItem key={rev.id} value={rev.id}>
                          {rev.revision_code} {rev.is_active && '(Attiva)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantita *</Label>
                  <Input
                    type="number"
                    value={lineForm.quantity}
                    onChange={(e) => setLineForm({ ...lineForm, quantity: e.target.value })}
                    required
                    min="1"
                    data-testid="line-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prezzo Unitario</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={lineForm.unit_price}
                    onChange={(e) => setLineForm({ ...lineForm, unit_price: e.target.value })}
                    data-testid="line-unit-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Consegna Richiesta</Label>
                <Input
                  type="date"
                  value={lineForm.delivery_date_required}
                  onChange={(e) => setLineForm({ ...lineForm, delivery_date_required: e.target.value })}
                  data-testid="line-delivery-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Lotto Cliente</Label>
                <Input
                  value={lineForm.customer_lot}
                  onChange={(e) => setLineForm({ ...lineForm, customer_lot: e.target.value })}
                  placeholder="Es. PROD-2026-7845 (opzionale, conto lavoro)"
                  data-testid="line-customer-lot-input"
                />
                <p className="text-xs text-slate-500">Numero di produzione / lotto fornito dal cliente. Verrà ereditato da commesse e spedizioni.</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLineDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="save-line-btn">Aggiungi Riga</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Line Dialog */}
      <Dialog open={editLineDialogOpen} onOpenChange={setEditLineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Riga Ordine</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateLine}>
            <div className="space-y-4 py-4">
              {selectedLine && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Prodotto: <span className="font-medium font-mono">{selectedLine.product_code}</span>
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantita</Label>
                  <Input
                    type="number"
                    value={editLineForm.quantity}
                    onChange={(e) => setEditLineForm({ ...editLineForm, quantity: e.target.value })}
                    min="1"
                    data-testid="edit-line-quantity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prezzo Unitario</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editLineForm.unit_price}
                    onChange={(e) => setEditLineForm({ ...editLineForm, unit_price: e.target.value })}
                    data-testid="edit-line-unit-price"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data Consegna Richiesta</Label>
                <Input
                  type="date"
                  value={editLineForm.delivery_date_required}
                  onChange={(e) => setEditLineForm({ ...editLineForm, delivery_date_required: e.target.value })}
                  data-testid="edit-line-delivery-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Lotto Cliente</Label>
                <Input
                  value={editLineForm.customer_lot}
                  onChange={(e) => setEditLineForm({ ...editLineForm, customer_lot: e.target.value })}
                  placeholder="Es. PROD-2026-7845 (opzionale)"
                  data-testid="edit-line-customer-lot"
                />
                {selectedLine?.production_lots?.length > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="customer-lot-history-warning">
                    Attenzione: esistono già commesse/spedizioni collegate a questa riga. Il Lotto Cliente storico resta congelato sui documenti già generati; la modifica vale solo per i documenti futuri.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditLineDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="update-line-btn">Aggiorna</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Lot Dialog */}
      <Dialog open={lotDialogOpen} onOpenChange={setLotDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crea Lotto di Produzione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLot}>
            <div className="space-y-4 py-4">
              {selectedLine && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Prodotto: <span className="font-medium">{selectedLine.product_code}</span>
                  </p>
                  <p className="text-sm text-slate-600">
                    Quantita Ordine: <span className="font-medium">{selectedLine.quantity}</span>
                  </p>
                  <p className="text-sm text-slate-600">
                    Lotto Cliente: <span className="font-mono font-medium" data-testid="lot-dialog-customer-lot">{selectedLine.customer_lot || '-'}</span>
                    <span className="text-xs text-slate-400 ml-2">(ereditato automaticamente)</span>
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantita Lotto *</Label>
                  <Input
                    type="number"
                    value={lotForm.quantity}
                    onChange={(e) => setLotForm({ ...lotForm, quantity: e.target.value })}
                    required
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Apertura *</Label>
                  <Input
                    type="date"
                    value={lotForm.opening_date}
                    onChange={(e) => setLotForm({ ...lotForm, opening_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Operatore Responsabile</Label>
                <Input
                  value={lotForm.responsible_operator}
                  onChange={(e) => setLotForm({ ...lotForm, responsible_operator: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLotDialogOpen(false)}>Annulla</Button>
              <Button type="submit">Crea Lotto</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
